import React, { useEffect, useState } from 'react';
import { Clock, User as UserIcon } from 'lucide-react';
import { Pagination } from '../../../shared/components/Pagination';
import { useHistoricoCliente } from '../hooks';
import type { HistoryAction, HistoryEntry, ListHistoryParams } from '../api';

interface Props {
  clientId: string;
}

const DEFAULT_PAGE_SIZE = 25;

const ACTION_LABEL: Record<HistoryAction, string> = {
  'client.created': 'Cliente criado',
  'client.updated': 'Dados atualizados',
  'client.documento_changed': 'Documento (CNPJ/CPF) alterado',
  'client.activated': 'Cliente ativado',
  'client.inactivated': 'Cliente inativado',
  'client.suspended': 'Cliente suspenso',
  'client.blocked': 'Cliente bloqueado',
  'client.closed': 'Cliente encerrado',
  'client.reactivated': 'Cliente reativado',
  'client.deleted': 'Cliente excluído',
  'address.created': 'Endereço adicionado',
  'address.updated': 'Endereço atualizado',
  'address.deleted': 'Endereço removido',
  'address.set_principal': 'Endereço definido como principal',
};

const ACTION_BADGE: Record<HistoryAction, string> = {
  'client.created': 'bg-blue-100 text-blue-800',
  'client.updated': 'bg-gray-100 text-gray-800',
  'client.documento_changed': 'bg-purple-100 text-purple-800',
  'client.activated': 'bg-green-100 text-green-800',
  'client.inactivated': 'bg-gray-100 text-gray-700',
  'client.suspended': 'bg-orange-100 text-orange-800',
  'client.blocked': 'bg-red-100 text-red-800',
  'client.closed': 'bg-gray-200 text-gray-800',
  'client.reactivated': 'bg-emerald-100 text-emerald-800',
  'client.deleted': 'bg-red-200 text-red-900',
  'address.created': 'bg-sky-100 text-sky-800',
  'address.updated': 'bg-sky-50 text-sky-700',
  'address.deleted': 'bg-rose-100 text-rose-800',
  'address.set_principal': 'bg-yellow-100 text-yellow-800',
};

// Campos filtráveis mais úteis (subset do shape do Client + endereço).
const FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Todos os campos' },
  { value: 'razaoSocial', label: 'Razão Social' },
  { value: 'nomeFantasia', label: 'Nome Fantasia' },
  { value: 'documento', label: 'CNPJ / CPF' },
  { value: 'situacaoCadastral', label: 'Situação Cadastral' },
  { value: 'segmentoMercado', label: 'Segmento' },
  { value: 'porte', label: 'Porte' },
  { value: 'classificacao', label: 'Classificação' },
  { value: 'origem', label: 'Origem' },
  { value: 'inscricaoEstadual', label: 'Inscrição Estadual' },
  { value: 'inscricaoMunicipal', label: 'Inscrição Municipal' },
  { value: 'cnaePrincipal', label: 'CNAE Principal' },
  { value: 'logradouro', label: 'Endereço: Logradouro' },
  { value: 'cep', label: 'Endereço: CEP' },
  { value: 'principal', label: 'Endereço: Principal' },
];

const ACTION_OPTIONS: { value: '' | HistoryAction; label: string }[] = [
  { value: '', label: 'Todas as ações' },
  ...Object.entries(ACTION_LABEL).map(([value, label]) => ({
    value: value as HistoryAction,
    label,
  })),
];

/**
 * Painel "Histórico" do detalhe do cliente (SPEC 01 Fase 3). Lista
 * eventos ordenados do mais recente ao mais antigo, com filtros por
 * período, ação e campo. Cada linha expõe um resumo; clicar em "ver
 * detalhes" abre o diff before/after em JSON.
 */
export const ClienteHistorico: React.FC<Props> = ({ clientId }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [action, setAction] = useState<'' | HistoryAction>('');
  const [field, setField] = useState('');
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const params: ListHistoryParams = {
    limit,
    offset,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59.999Z`).toISOString() : undefined,
    action: action || undefined,
    field: field || undefined,
  };

  // Reset offset quando filtros mudam.
  useEffect(() => {
    setOffset(0);
  }, [from, to, action, field]);

  const { data, isLoading, isError, error } = useHistoricoCliente(clientId, params);
  const items: HistoryEntry[] = data?.data ?? [];
  const count = data?.pagination?.count ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Clock size={18} className="text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Histórico de alterações</h3>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-gray-500">De</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            aria-label="Filtrar histórico de"
            className="rounded-md border border-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-gray-500">Até</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            aria-label="Filtrar histórico até"
            className="rounded-md border border-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </label>
        {/* `<select>` nativo mantido intencionalmente: os testes E2E
            usam `.selectOption()` do Playwright, que só funciona com
            `<select>` HTML. Trocar por `AutocompleteSelect` (combobox
            do HeadlessUI) quebra `clientes-cadastro.spec.ts:735`. Se
            um dia migrar, adaptar o teste também. */}
        <label className="flex flex-col gap-1 text-sm text-gray-700">
          <span className="text-xs text-gray-500">Ação</span>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value as '' | HistoryAction)}
            aria-label="Filtrar histórico por ação"
            className="rounded-md border border-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {ACTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm text-gray-700">
          <span className="text-xs text-gray-500">Campo</span>
          <select
            value={field}
            onChange={(e) => setField(e.target.value)}
            aria-label="Filtrar histórico por campo"
            className="rounded-md border border-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {FIELD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Carregando histórico…</div>
      ) : isError ? (
        <div className="text-sm text-red-700">Erro: {(error as Error)?.message}</div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
          Nenhum evento para os filtros atuais.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data/hora</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ação</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campos alterados</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Motivo</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((h) => {
                const expanded = expandedId === h.id;
                return (
                  <React.Fragment key={h.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                        {new Date(h.createdAt).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {h.userName ? (
                          <div className="flex items-center gap-1">
                            <UserIcon size={12} className="text-gray-400" />
                            {h.userName}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_BADGE[h.action]}`}>
                          {ACTION_LABEL[h.action]}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600">
                        {h.changedFields.length === 0
                          ? '—'
                          : h.changedFields.slice(0, 3).join(', ') +
                            (h.changedFields.length > 3 ? ` +${h.changedFields.length - 3}` : '')}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600 max-w-xs truncate">
                        {h.reason ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : h.id)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          {expanded ? 'Ocultar' : 'Ver detalhes'}
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-gray-50">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 text-xs">
                            <div>
                              <div className="mb-1 font-medium text-gray-700">Antes</div>
                              <pre className="max-h-64 overflow-auto rounded border border-gray-200 bg-white p-2 font-mono text-xs">
                                {h.oldValue ? JSON.stringify(h.oldValue, null, 2) : '(vazio)'}
                              </pre>
                            </div>
                            <div>
                              <div className="mb-1 font-medium text-gray-700">Depois</div>
                              <pre className="max-h-64 overflow-auto rounded border border-gray-200 bg-white p-2 font-mono text-xs">
                                {h.newValue ? JSON.stringify(h.newValue, null, 2) : '(vazio)'}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <Pagination
            offset={offset}
            limit={limit}
            count={count}
            itemLabel="evento"
            itemLabelPlural="eventos"
            onChange={({ offset: o, limit: l }) => { setOffset(o); setLimit(l); }}
          />
        </div>
      )}
    </div>
  );
};
